import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import OpenSeadragon from 'openseadragon';
import type { TileSourceOptions } from 'openseadragon';

@Component({
  selector: 'ts-dzi-viewer',
  templateUrl: './dzi-viewer.component.html',
  styleUrls: ['./dzi-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DziViewerComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('openseadragonContainer', { static: true })
  openseadragonContainer!: ElementRef<HTMLDivElement>;
  private viewer!: OpenSeadragon.Viewer;
  private lastOpenedTileSource: TileSourceOptions | null = null;

  @Input() zoomLevel = 1;
  @Input() focusPoint: { x: number; y: number };
  @Input() tileSource: TileSourceOptions;
  @Input() showNavigator = false;
  @Input() navigatorPosition: 'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'ABSOLUTE' = 'BOTTOM_RIGHT';
  @Input() cursorClass = '';

  @Output() zoomLevelChanged = new EventEmitter<number>();
  @Output() focusPointChanged = new EventEmitter<{ x: number; y: number }>();
  /** Emitted when the viewer finishes opening a tile source (image is shown). */
  @Output() opened = new EventEmitter<void>();

  private userInteracting = false;

  ngOnInit(): void {
    this.initializeViewer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewer) return;

    let shouldUpdateTileSource = false;
    let shouldReposition = false;

    for (const key in changes) {
      const { currentValue, previousValue } = changes[key];

      if (currentValue === previousValue || !currentValue) continue;

      switch (key) {
        case 'tileSource':
          shouldUpdateTileSource = true;
          break;
        case 'zoomLevel':
          shouldReposition = true;
          break;
        case 'focusPoint':
          shouldReposition = true;
          break;
        default:
          break;
      }
    }

    if (shouldUpdateTileSource) this.updateTileSource();
    if (shouldReposition && !shouldUpdateTileSource) this.goToFocusPoint();
  }

  ngOnDestroy(): void {
    // Destroy the viewer when the component is destroyed
    if (this.viewer) this.viewer.destroy();
  }

  private initializeViewer(): void {
    this.viewer = new OpenSeadragon.Viewer({
      element: this.openseadragonContainer.nativeElement,
      prefixUrl: 'assets/icons/',
      immediateRender: false,
      springStiffness: 65, //x10 default value
      placeholderFillStyle: '#303030',
      timeout: 30000,
      animationTime: 1.5,
      autoHideControls: false,
      showNavigator: this.showNavigator,
      navigatorPosition: this.navigatorPosition,
      navigatorHeight: '150px',
      navigatorWidth: '150px',
      showNavigationControl: false,
      showZoomControl: false,
      showSequenceControl: false,
      preload: true,
      sequenceMode: true,
      initialPage: 0,
      clickDistThreshold: 100,
      gestureSettingsMouse: {
        clickToZoom: false,
      },
      minZoomImageRatio: 0.1,
      loadTilesWithAjax: true,
    });

    this.viewer.addHandler('open', () => {
      this.goToFocusPoint();
      this.opened.emit();
    });
    this.viewer.addHandler('canvas-scroll', () => (this.userInteracting = true));
    this.viewer.addHandler('canvas-pinch', () => (this.userInteracting = true));
    this.viewer.addHandler('canvas-drag-end', () => (this.userInteracting = true));
    this.viewer.addHandler('animation-finish', () => this.emitUserViewportState());

    this.updateTileSource(); // Set the initial tile source if provided
  }

  private emitUserViewportState(): void {
    if (!this.userInteracting) return;
    this.userInteracting = false;
    this.zoomLevelChanged.emit(this.viewer.viewport.getZoom());
    const center = this.viewer.viewport.getCenter();
    const imageCenter = this.viewer.viewport.viewportToImageCoordinates(center.x, center.y);
    this.focusPointChanged.emit({ x: imageCenter.x, y: imageCenter.y });
  }

  private goToFocusPoint(): void {
    if (!this.focusPoint) return;

    // obtains point coordinates relative to view port
    const point = this.viewer.viewport.imageToViewportCoordinates(this.focusPoint.x, this.focusPoint.y);

    // Zoom + pan IMMEDIATELY (no spring animation). On navigation the focus
    // point jumps to the new image's centre; animating it produced a visible
    // auto-scroll/glide that felt odd. `immediately = true` snaps instead.
    this.viewer.viewport.zoomTo(this.zoomLevel, undefined, true);
    this.viewer.viewport.panTo(point, true);
  }

  private updateTileSource() {
    if (!this.tileSource) {
      this.lastOpenedTileSource = null;
      this.viewer.close();
      return;
    }

    if (this.tileSource === this.lastOpenedTileSource) return;
    this.lastOpenedTileSource = this.tileSource;

    // Make the open-time `goHome` land directly at our target zoom, so the
    // image doesn't first fit ("home" zoom) and then jump to the focus zoom —
    // that two-step reposition was the visible downward shift on navigation.
    // `defaultZoomLevel` exists on OSD's Viewport at runtime but is missing
    // from the @types/openseadragon 2.4 definitions, hence the cast.
    if (this.zoomLevel) {
      (this.viewer.viewport as unknown as { defaultZoomLevel: number }).defaultZoomLevel = this.zoomLevel;
    }

    this.viewer.open({
      ajaxHeaders: this.tileSource.ajaxHeaders,
      tileSource: { ...this.tileSource },
    });
  }
}
